import {useAsync2, useAsyncAction} from "nate-react-api-helpers";
import {api} from "../../../api/API";
import {useProjectId} from "./ProjectName";
import {ActionLoading, Loader} from "../../../misc/Loader";
import {createContext, PropsWithChildren, useContext, useEffect, useMemo, useState} from "react";
import {useSyncedRef} from "../../../misc/SyncedRef";
import {ChangesetContext} from "./changeset/ChangesetContext";
import {ChangePreviewContext} from "./ShopDrawingChangePreview";
import {SessionTakeover} from "./changeset/SessionTakeover";
import {LockOpen} from "@mui/icons-material";
import {LockSharp} from "@mui/icons-material";
import AddCommentIcon from '@mui/icons-material/AddComment';
import {HeaderActionButton} from "./HeaderActionButton";
import {short} from "../quote/CurrentContractor";
import {AlertProductRecalcHeader} from "../AlertProductRecalcHeader";
import {useNavigate} from "react-router-dom";

export const ManagerLockContext = createContext({
    locked: false,
    onChange: (value: boolean) => {}
})

export function ManagerLockProvider(props: PropsWithChildren<{}>) {
    const [locked, setLocked] = useState(false);

    const ctx = useMemo(() => ({
        locked: locked,
        onChange: setLocked,
    }), [locked]);

    return (
        <ManagerLockContext.Provider value={ctx}>
            {props.children}
        </ManagerLockContext.Provider>
    );
}


export function ProjectActions(props: {
}) {
    const project = useProjectId();
    const info = useAsync2(input => api.projects.getShopDrawingMeta(input), {project: project}, [project]);

    const reloadRef = useSyncedRef(info.reload);
    const update = useAsyncAction(async (input) => {
        await api.projects.updateShopDrawingMeta(input);
        reloadRef.current();
    }, []);

    const ctx = useContext(ManagerLockContext);
    const managerLockChanged = ctx.onChange;
    const managerLockEnabled = info.result?.managerLockEnabled;
    useEffect(() => {
        if(managerLockEnabled === undefined) return;
        managerLockChanged(managerLockEnabled)
    }, [managerLockChanged, managerLockEnabled]);

    const [showSessionTakeover, setShowSessionTakeover] = useState(false);
    const nav = useNavigate();

    const changeset = useContext(ChangesetContext);
    const preview = useContext(ChangePreviewContext);

    if(preview.enabled) return null;

    const lockedToSomeoneElse = changeset.proposing && !changeset.isMine;
    return (
        <>
            {showSessionTakeover && <SessionTakeover onClose={() => setShowSessionTakeover(false)} />}
            <Loader {...info}>
                {value => (
                    <div style={{marginTop: 4, display: "flex", flexDirection: "row", gap: 0}}>
                        <ActionLoading {...update}>
                            <HeaderActionButton
                                label="Manager"
                                icon={value.managerLockEnabled ? <LockSharp fontSize="inherit" /> : <LockOpen fontSize="inherit" />}
                                badgeContent={value.managerLockEnabled ? "Locked" : null}
                                badgeColor="secondary"
                                onClick={async () => {
                                    update.callback(Object.assign({}, value, {
                                        managerLockEnabled: !value.managerLockEnabled,
                                    }))
                                }}
                            />
                        </ActionLoading>

                        <HeaderActionButton
                            label="Changes"
                            icon={<AddCommentIcon fontSize="inherit" color={lockedToSomeoneElse ? "error" : changeset.proposing ? "primary" : undefined} />}
                            badgeContent={lockedToSomeoneElse ? short(changeset.lockedToName, 8) : changeset.proposing ? "Active" : null}
                            onClick={async () => {
                                if (changeset.proposing) {
                                    if(lockedToSomeoneElse) {
                                        setShowSessionTakeover(true)
                                        return;
                                    }

                                    nav(`/project/${project}/logictrl/changes`);
                                } else {
                                    await api.projectChangeset.start({project: project});
                                }
                            }}
                        />

                        <AlertProductRecalcHeader />
                    </div>
                )}
            </Loader>
        </>
    )
}